import { getDisplayCurrency, TIME_CONSTANTS } from "@ccflare/core";

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(ms: number): string {
	if (ms < TIME_CONSTANTS.SECOND) return `${ms}ms`;
	if (ms < TIME_CONSTANTS.MINUTE)
		return `${(ms / TIME_CONSTANTS.SECOND).toFixed(1)}s`;
	if (ms < TIME_CONSTANTS.HOUR)
		return `${(ms / TIME_CONSTANTS.MINUTE).toFixed(1)}m`;
	return `${(ms / TIME_CONSTANTS.HOUR).toFixed(1)}h`;
}

/**
 * Format tokens with locale-aware thousands separator
 */
export function formatTokens(tokens?: number | null): string {
	if (!tokens || tokens === 0) return "0";
	return tokens.toLocaleString();
}

/**
 * Format percentage with specified decimal places
 */
export function formatPercentage(value: number, decimals = 1): string {
	return `${value.toFixed(decimals)}%`;
}

/**
 * Format number with locale-aware thousands separator
 */
export function formatNumber(value: number): string {
	return value.toLocaleString();
}

/**
 * Format timestamp to locale string
 */
export function formatTimestamp(timestamp: number | string): string {
	const date =
		typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
	return date.toLocaleString();
}

/**
 * Format tokens per second with 1 decimal place
 */
export function formatTokensPerSecond(tokensPerSecond?: number | null): string {
	if (!tokensPerSecond || tokensPerSecond === 0) return "0 tok/s";
	return `${tokensPerSecond.toFixed(1)} tok/s`;
}

/**
 * Compact token count, e.g. 1_240_000 -> "1.24M", 9500 -> "9.5K", 0 -> "0".
 */
export function formatTokensCompact(tokens?: number | null): string {
	if (!tokens || tokens === 0) return "0";
	return new Intl.NumberFormat("en-US", {
		notation: "compact",
		maximumFractionDigits: tokens < 1000 ? 0 : 2,
	}).format(tokens);
}

/**
 * Short cost with 2 decimals in the active display currency, converting from
 * the stored USD amount, e.g. 3.4 USD -> "€3.13", 0 -> "€0.00".
 */
export function formatCostShort(cost?: number | null): string {
	const { symbol, rate } = getDisplayCurrency();
	if (!cost || cost === 0) return `${symbol}0.00`;
	return `${symbol}${(cost * rate).toFixed(2)}`;
}

/**
 * Human countdown from a millisecond duration:
 * 14_700_000 -> "4h 5m", 600_000 -> "10m", <= 0 -> "now".
 */
export function formatCountdown(ms: number): string {
	if (ms <= 0) return "now";
	const totalMinutes = Math.ceil(ms / 60000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
